import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from 'react-i18next';
import { usePageTitle } from "@/hooks/use-page-title";
import { useLocation, useParams } from "wouter";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { usePublicConditionWallets, QUERY_KEYS } from "@/hooks/use-queries";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useToast } from "@/hooks/use-toast";
import {
  Heart,
  Users,
  TrendingUp,
  DollarSign,
  ArrowRight,
  Target,
  Gift,
  Stethoscope,
  Baby,
  Pill,
  Activity,
  CircleDollarSign,
  CheckCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageAbout } from "@/components/page-about";

interface CommunityFund {
  id: string;
  conditionId: string;
  title: string;
  summary?: string;
  fundingGoal: number | null;
  totalRaised: number;
  memberCount: number;
  contributorCount: number;
  servicesDelivered: number;
  createdAt: string;
}

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatConditionId(conditionId: string): string {
  return conditionId
    .replace(/^mc-/, "")
    .replace(/-/g, " ")
    .replace(/\b\w/g, c => c.toUpperCase());
}

function getCategoryIcon(conditionId: string) {
  const id = conditionId?.toLowerCase() ?? "";
  if (id.includes("cancer") || id.includes("oncolog")) return <Activity className="h-5 w-5 text-pink-500" />;
  if (id.includes("pediatric") || id.includes("child")) return <Baby className="h-5 w-5 text-blue-400" />;
  if (id.includes("diabetes")) return <Pill className="h-5 w-5 text-purple-500" />;
  if (id.includes("transplant") || id.includes("organ")) return <Stethoscope className="h-5 w-5 text-green-500" />;
  if (id.includes("rare") || id.includes("disease")) return <Target className="h-5 w-5 text-orange-500" />;
  return <Heart className="h-5 w-5 text-red-500" />;
}

function getCategoryColor(conditionId: string): string {
  const id = conditionId?.toLowerCase() ?? "";
  if (id.includes("cancer") || id.includes("oncolog")) return "bg-pink-500/10 text-pink-700 dark:text-pink-300";
  if (id.includes("pediatric") || id.includes("child")) return "bg-blue-500/10 text-blue-700 dark:text-blue-300";
  if (id.includes("diabetes")) return "bg-purple-500/10 text-purple-700 dark:text-purple-300";
  if (id.includes("transplant") || id.includes("organ")) return "bg-green-500/10 text-green-700 dark:text-green-300";
  if (id.includes("rare") || id.includes("disease")) return "bg-orange-500/10 text-orange-700 dark:text-orange-300";
  return "bg-muted text-muted-foreground";
}

const PRESET_AMOUNTS = [1000, 2500, 5000, 10000, 25000];

const contributionFormSchema = z.object({
  contributorName: z.string().optional(),
  contributorEmail: z.string().email("Invalid email address").optional().or(z.literal("")),
  amount: z.number().int().min(100, "Minimum contribution is $1.00"),
});

type ContributionFormValues = z.infer<typeof contributionFormSchema>;

export default function CommunityFundsPage() {
  const { t } = useTranslation();
  usePageTitle("Community Health Funds", "Explore condition-based community funds. Pool resources with others to cover shared healthcare costs.");
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [selectedFund, setSelectedFund] = useState<CommunityFund | null>(null);
  const [selectedPresetAmount, setSelectedPresetAmount] = useState<number>(2500);
  const [customAmount, setCustomAmount] = useState<string>("");
  const [isContributeDialogOpen, setIsContributeDialogOpen] = useState(false);

  const form = useForm<ContributionFormValues>({
    mode: 'onBlur',
    resolver: zodResolver(contributionFormSchema),
    defaultValues: {
      contributorName: "",
      contributorEmail: "",
      amount: 2500,
    },
  });

  const { data: funds, isLoading } = usePublicConditionWallets();

  const checkoutMutation = useMutation({
    mutationFn: async ({ fundId, amount, contributorName, contributorEmail }: {
      fundId: string;
      amount: number;
      contributorName: string;
      contributorEmail: string;
    }) => {
      const res = await apiRequest("POST", `/api/condition-wallets/${fundId}/contribute/checkout`, {
        amount,
        contributorName,
        contributorEmail,
      });
      return res.json();
    },
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Contribution failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleContribute = (fund: CommunityFund) => {
    setSelectedFund(fund);
    setSelectedPresetAmount(2500);
    setCustomAmount("");
    form.reset({
      contributorName: "",
      contributorEmail: "",
      amount: 2500,
    });
    setIsContributeDialogOpen(true);
  };

  const handleAmountSelect = (amount: number) => {
    setSelectedPresetAmount(amount);
    setCustomAmount("");
    form.setValue("amount", amount);
  };

  const handleCustomAmountChange = (value: string) => {
    setCustomAmount(value);
    const amountInCents = Math.round(parseFloat(value || "0") * 100);
    if (amountInCents > 0) {
      form.setValue("amount", amountInCents);
    }
  };

  const onSubmit = (data: ContributionFormValues) => {
    if (!selectedFund) return;
    
    const amount = customAmount ? Math.round(parseFloat(customAmount) * 100) : selectedPresetAmount;
    
    checkoutMutation.mutate({
      fundId: selectedFund.id,
      amount,
      contributorName: data.contributorName || "",
      contributorEmail: data.contributorEmail || "",
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-8 w-64 mb-2" />
            <Skeleton className="h-4 w-96" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-32 mb-2" />
                <Skeleton className="h-4 w-48" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-24 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const activeFunds = funds || [];
  const totalRaisedAcrossAll = funds?.reduce((sum, f) => sum + (f.totalRaised || 0), 0) || 0;
  const totalServicesDelivered = funds?.reduce((sum, f) => sum + (f.servicesDelivered || 0), 0) || 0;

  return (
    <div className="space-y-8">
      <PageAbout
        title={t('community-funds.heading')}
        description={t('community-funds.about-body')}
        features={[
          "Disease-specific funds for oncology, pediatric, diabetes, and more",
          "100% of contributions go directly to patient assistance",
          "Track how much has been raised and distributed for each fund",
          "Contribute anonymously or share your support publicly",
          "Secure payment processing through Stripe"
        ]}
        tips={[
          "Even small contributions make a big difference when combined",
          "Set up recurring contributions to maximize community impact",
          "Share fund links with your network to multiply the impact"
        ]}
      />
      
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <Users className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold">{t('community-funds.heading')}</h1>
            <p className="text-muted-foreground">
              {t('community-funds.subheading')}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">{t('community-funds.stat-total-raised')}</p>
                <p className="text-2xl font-bold text-primary" data-testid="text-total-raised">
                  {formatCents(totalRaisedAcrossAll)}
                </p>
              </div>
              <div className="p-3 rounded-full bg-green-100 dark:bg-green-900/30">
                <TrendingUp className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">{t('community-funds.stat-services-delivered')}</p>
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400" data-testid="text-total-distributed">
                  {totalServicesDelivered.toLocaleString()}
                </p>
              </div>
              <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900/30">
                <Gift className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">{t('community-funds.stat-active-funds')}</p>
                <p className="text-2xl font-bold" data-testid="text-active-funds">
                  {activeFunds.length}
                </p>
              </div>
              <div className="p-3 rounded-full bg-purple-100 dark:bg-purple-900/30">
                <Target className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Separator />

      <div>
        <h2 className="text-xl font-semibold mb-4">{t('community-funds.section-browse-heading')}</h2>
        
        {activeFunds.length === 0 ? (
          <Card className="p-8 text-center">
            <div className="flex flex-col items-center gap-4">
              <div className="p-4 rounded-full bg-muted">
                <Heart className="h-8 w-8 text-muted-foreground" />
              </div>
              <div>
                <h3 className="text-lg font-medium mb-1">No Active Funds</h3>
                <p className="text-muted-foreground">
                  There are no community funds available at this time. Check back later.
                </p>
              </div>
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {activeFunds.map((fund) => (
              <Card 
                key={fund.id} 
                className="hover-elevate transition-all duration-200"
                data-testid={`card-fund-${fund.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-muted">
                        {getCategoryIcon(fund.conditionId)}
                      </div>
                      <div>
                        <CardTitle className="text-lg" data-testid={`text-fund-name-${fund.id}`}>
                          {fund.title}
                        </CardTitle>
                        <Badge 
                          variant="secondary" 
                          className={getCategoryColor(fund.conditionId)}
                        >
                          {formatConditionId(fund.conditionId)}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  {fund.summary && (
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {fund.summary}
                    </p>
                  )}
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t('community-funds.stat-total-raised')}</span>
                      <span className="font-medium text-primary">{formatCents(fund.totalRaised)}</span>
                    </div>
                    <Progress 
                      value={fund.fundingGoal && fund.fundingGoal > 0 ? Math.min((fund.totalRaised / fund.fundingGoal) * 100, 100) : 0} 
                      className="h-2"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>{(fund.servicesDelivered || 0).toLocaleString()} {t('community-funds.fund-card.services-delivered')}</span>
                      <span>{fund.fundingGoal ? t('community-funds.fund-card.goal-prefix', { amount: formatCents(fund.fundingGoal) }) : ""}</span>
                    </div>
                  </div>
                </CardContent>
                
                <CardFooter className="pt-0">
                  <Button 
                    className="w-full gap-2" 
                    onClick={() => handleContribute(fund)}
                    data-testid={`button-contribute-${fund.id}`}
                  >
                    <Heart className="h-4 w-4" />
                    {t('common.cta.contribute-to-fund')}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog open={isContributeDialogOpen} onOpenChange={setIsContributeDialogOpen}>
        <DialogContent className="max-w-md" data-testid="dialog-contribute">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Heart className="h-5 w-5 text-primary" />
              Contribute to {selectedFund?.title}
            </DialogTitle>
            <DialogDescription>
              Your contribution will help patients in the {selectedFund ? formatConditionId(selectedFund.conditionId) : ""} community access essential healthcare.
            </DialogDescription>
          </DialogHeader>
          
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 py-4">
              <div className="space-y-3">
                <Label>Select Amount</Label>
                <div className="grid grid-cols-3 gap-2">
                  {PRESET_AMOUNTS.map((amount) => (
                    <Button
                      key={amount}
                      type="button"
                      variant={selectedPresetAmount === amount && !customAmount ? "default" : "outline"}
                      className="h-12"
                      onClick={() => handleAmountSelect(amount)}
                      data-testid={`button-amount-${amount}`}
                    >
                      {formatCents(amount)}
                    </Button>
                  ))}
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="number"
                      placeholder="Other"
                      value={customAmount}
                      onChange={(e) => handleCustomAmountChange(e.target.value)}
                      className="h-12 pl-8"
                      data-testid="input-custom-amount"
                    />
                  </div>
                </div>
              </div>

              <FormField
                control={form.control}
                name="contributorName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Your Name (optional)</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="John Doe"
                        {...field}
                        data-testid="input-contributor-name"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="contributorEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email (optional, for receipt)</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="john@example.com"
                        {...field}
                        data-testid="input-contributor-email"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Separator />

              <div 
                className="flex items-center justify-between p-4 bg-muted/50 rounded-lg"
                data-testid="display-total-amount"
              >
                <span className="font-medium">Total Contribution</span>
                <span className="text-2xl font-bold text-primary">
                  {customAmount ? `$${parseFloat(customAmount || "0").toFixed(2)}` : formatCents(selectedPresetAmount)}
                </span>
              </div>

              <Button
                type="submit"
                className="w-full gap-2"
                size="lg"
                disabled={checkoutMutation.isPending}
                data-testid="button-submit-contribution"
              >
                {checkoutMutation.isPending ? (
                  "Processing..."
                ) : (
                  <>
                    <CircleDollarSign className="h-5 w-5" />
                    Proceed to Checkout
                  </>
                )}
              </Button>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function CommunityFundSuccessPage() {
  const params = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [verified, setVerified] = useState(false);

  const urlParams = new URLSearchParams(window.location.search);
  const sessionId = urlParams.get("session_id");

  const verifyMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/condition-wallets/${params.id}/contribute/verify`, {
        sessionId,
      });
      return res.json();
    },
    onSuccess: () => {
      setVerified(true);
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.publicConditionWallets });
      toast({
        title: "Contribution successful!",
        description: "Thank you for supporting our community healthcare fund.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Verification failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  if (!verified && sessionId && !verifyMutation.isPending) {
    verifyMutation.mutate();
  }

  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Card className="max-w-md w-full text-center p-8">
        <div className="flex flex-col items-center gap-4">
          <div className="p-4 rounded-full bg-green-100 dark:bg-green-900/30">
            <CheckCircle className="h-12 w-12 text-green-600 dark:text-green-400" />
          </div>
          <h2 className="text-2xl font-bold">Thank You!</h2>
          <p className="text-muted-foreground">
            Your contribution to the community fund has been received. Together, we're making healthcare more accessible.
          </p>
          <Button 
            className="mt-4 gap-2"
            onClick={() => setLocation("/community-funds")}
            data-testid="button-back-to-funds"
          >
            <ArrowRight className="h-4 w-4" />
            Back to Community Funds
          </Button>
        </div>
      </Card>
    </div>
  );
}
